import { Dispatch, PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { GetVaccinesParams, PaginatedResponse, Pagination, Vaccine, VaccineListItem, typicodeApi } from 'shared/api';

type TVaccineState = {
  list: Vaccine[];
  vaccineList: VaccineListItem[];
  pagination: Pagination | null;
};

const initialState: TVaccineState = {
  list: [],
  vaccineList: [],
  pagination: null,
};

export const vaccineModel = createSlice({
  name: 'vaccines',
  initialState,
  reducers: {
    setVaccines: (state, { payload }: PayloadAction<Vaccine[]>) => {
      state.list = payload;
    },
    setVaccineList: (state, { payload }: PayloadAction<VaccineListItem[]>) => {
      state.vaccineList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    addVaccines: (state, { payload }: PayloadAction<Vaccine>) => {
      state.list = [...state.list, payload];
    },
    updateDiseases: (state, { payload }: PayloadAction<Vaccine>) => {
      state.list = state.list.map((item) => (item._id === payload._id ? payload : item));
    },
  },
});

// export const {} = vaccineModel.actions;

// react-query actions (everything that async)
export const VACCINES_ALL_QUERY_KEY = 'vaccines-all';
const VACCINE_BY_ID_QUERY_KEY = 'vaccine-by-id';

export const getVaccinesAllAsync = (params: GetVaccinesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<VaccineListItem>>>(
    [VACCINES_ALL_QUERY_KEY, params],
    () => typicodeApi.vaccine.getVaccinesAll(params),
    {
      onSuccess: ({ data }) => {
        dispatch(vaccineModel.actions.setVaccineList(data.data));
        dispatch(vaccineModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('There was an error loading the list of vaccines, please try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getVaccineByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<Vaccine>>([VACCINE_BY_ID_QUERY_KEY, id], () => typicodeApi.vaccine.getVaccineById(id), {
    enabled,
    staleTime: 0, // Данные сразу считаются устаревшими - всегда свежие при открытии
    cacheTime: 0, // Не кешировать - каждый раз загружать заново
    refetchOnWindowFocus: false,
  });

export const postNewVaccineAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.vaccine.postVaccine, {
    onSuccess: () => {
      toast.success('The vaccine has been successfully added!');
      void queryClient.invalidateQueries(VACCINES_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when adding a new vaccine. Try again!');
    },
  });
};

export const updateVaccineAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.vaccine.patchVaccine, {
    onSuccess: () => {
      toast.success('The vaccine has been successfully modified!');
      void queryClient.invalidateQueries(VACCINES_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when changing the vaccine. Try again!');
    },
  });
};

// selectors
// export const useVaccines = () => useSelector((state: RootState) => state.vaccines);

export const useVaccineList = () =>
  useSelector((state: RootState) => ({
    vaccineList: state.vaccines.vaccineList,
    pagination: state.vaccines.pagination,
  }));
export const isVaccinesEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.vaccines.list,
      (vaccines) => vaccines.length === 0,
    ),
  );
export const isVaccineListEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.vaccines.vaccineList,
      (vaccines) => vaccines.length === 0,
    ),
  );
export const reducer = vaccineModel.reducer;
